import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { useViewport } from 'hooks/use-viewport';

import { dynamicComposedComponent } from 'helpers/importer/dynamicComposedComponent';

import { Button } from 'core/generic/elements/button';
import { Icon, IconVariants } from 'core/generic/elements/icons';
import { InfoBlock } from 'core/generic/elements/info-block';
import { MenuItem } from 'core/generic/elements/menu-item';

import { LOCALE_SELECTOR_ITEMS } from './locale-selector-data';
import styles from './locale-selector.module.scss';

const SheetWrapper = dynamicComposedComponent(
	() => import('core/generic/components/general/sheet')
);

type LocaleSelectorProps = {
	visible: boolean;
	setVisible: (visible: boolean) => void;
};

export const LocaleSelector = ({
	visible,
	setVisible,
}: LocaleSelectorProps) => {
	const { t, i18n } = useTranslation();
	const { isTablet, isDesktop } = useViewport();
	return (
		<Suspense fallback={null}>
			<SheetWrapper>
				{(Sheet) => (
					<Sheet.Root
						visible={visible}
						setVisible={setVisible}
						size={isDesktop ? 'fit' : 'ninetyFive'}
						ariaLabel="Locale selector"
					>
						<Sheet.Header>
							<div className={styles['header']}>
								<div className={styles['header__content']}>
									<div className={styles['header__title']}>
										<Icon variant="Language"></Icon>
										<span className="title-small">
											{t(
												'global.locale_selector:choose_language_title',
												'Choose language'
											)}
										</span>
									</div>
									<div
										className={
											styles['header__description']
										}
									>
										<span className="body-medium">
											{t(
												'global.locale_selector:choose_language',
												'Choose your website language, or follow us on social media in your language.'
											)}
										</span>
									</div>
								</div>
							</div>
						</Sheet.Header>
						<Sheet.Body>
							<div className={styles['body']}>
								<InfoBlock variant="info" display="stacked">
									<div className={styles['body__info-block']}>
										<span className="body-small">
											{t(
												'global.locale_selector:not_all_content_available',
												'Not all content is available in all languages, but key information is provided where possible.'
											)}
										</span>
										<span className="body-small medium">
											{t(
												'global.locale_selector:redirect_homepage',
												'You’ll be redirected to the homepage after making your selection.'
											)}
										</span>
									</div>
								</InfoBlock>
								<ul className={styles['locales-list']}>
									{LOCALE_SELECTOR_ITEMS.map((item) => (
										<li
											key={item.lang}
											className={
												styles['locales-list__item']
											}
										>
											<MenuItem
												label={item.label}
												href={item.href}
												active={
													(i18n.language ?? 'en') ===
													item.lang
												}
												trailingElement={
													(i18n.language ?? 'en') ===
													item.lang ? (
														<Icon variant="Check" />
													) : undefined
												}
											/>
											<div
												className={
													styles[
														'locales-list__socials'
													]
												}
											>
												{item.socials.map((social) => (
													<Button
														variant="link-button"
														key={social.title}
														href={social.href}
														icon={
															social.icon
																.variant as IconVariants
														}
														ariaLabel={social.title}
														target="_blank"
														dimension={
															isTablet ||
															isDesktop
																? 'large'
																: 'medium'
														}
													/>
												))}
											</div>
										</li>
									))}
								</ul>
							</div>
						</Sheet.Body>
					</Sheet.Root>
				)}
			</SheetWrapper>
		</Suspense>
	);
};
